// src/hooks/useWidgetPreferences.ts
import { useState, useCallback, useEffect } from 'react';
import type { WidgetPreferences } from '../widgets/types';

const STORAGE_KEY = 'bill-tracker-widgets';

const defaultPreferences: WidgetPreferences = {
	enabledWidgets: [],
};

/**
 * Hook to manage widget preferences in localStorage
 */
export function useWidgetPreferences() {
	const [preferences, setPreferences] = useState<WidgetPreferences>(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored) {
				return JSON.parse(stored);
			}
		} catch (error) {
			console.error('Failed to load widget preferences:', error);
		}
		return defaultPreferences;
	});

	// Persist to localStorage when preferences change
	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
		} catch (error) {
			console.error('Failed to save widget preferences:', error);
		}
	}, [preferences]);

	const addWidget = useCallback((widgetId: string) => {
		setPreferences((prev) => {
			if (prev.enabledWidgets.includes(widgetId)) {
				return prev;
			}
			return {
				...prev,
				enabledWidgets: [...prev.enabledWidgets, widgetId],
			};
		});
	}, []);

	const removeWidget = useCallback((widgetId: string) => {
		setPreferences((prev) => ({
			...prev,
			enabledWidgets: prev.enabledWidgets.filter((id) => id !== widgetId),
		}));
	}, []);

	const toggleWidget = useCallback((widgetId: string) => {
		setPreferences((prev) => {
			if (prev.enabledWidgets.includes(widgetId)) {
				return {
					...prev,
					enabledWidgets: prev.enabledWidgets.filter(
						(id) => id !== widgetId
					),
				};
			}
			return {
				...prev,
				enabledWidgets: [...prev.enabledWidgets, widgetId],
			};
		});
	}, []);

	const isWidgetEnabled = useCallback(
		(widgetId: string) => {
			return preferences.enabledWidgets.includes(widgetId);
		},
		[preferences.enabledWidgets]
	);

	return {
		enabledWidgets: preferences.enabledWidgets,
		addWidget,
		removeWidget,
		toggleWidget,
		isWidgetEnabled,
	};
}
